import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { observer } from "mobx-react";
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack,
  DeleteOutline,
  EditOutlined,
  ImageOutlined,
  LocalShippingOutlined,
  LocationOnOutlined,
  PersonOutline,
  ReplayOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { CurrencyHelper } from "../../../../../utils/CurrencyHelper";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import ImageGalleryPopup from "../../../../../views/components/common/pop-up/ImageGalleryPopup";
import ConfirmPopup from "../../../../../views/components/common/pop-up/ConfirmPopup";
import { useStores } from "../../../use-stores";
import { SwayyOrderDetailAddress } from "../../../../../models/SwayyOrder/SwayyOrderDetail";
import {
  getSwayyOrderStatusLabel,
  getSwayyStatusTone,
} from "../../../../../models/SwayyOrder/SwayyOrderFilters";
import {
  canDeleteSwayyShipment,
  getSwayyShipmentStatusLabel,
  SWAYY_SHIPPABLE_ORDER_STATUSES,
} from "../../../../../models/SwayyShipment/SwayyShipmentFilters";
import {
  CreateSwayyShipmentPayload,
  getSwayyShipmentNumber,
  UpdateSwayyShipmentPayload,
} from "../../../../../models/SwayyShipment/SwayyShipment";
import { swayyColors, swayyFont, swayyRadii } from "../../../theme/tokens";
import { swayyCarrierStore } from "../../../stores/SwayyCarrierStore";
import { swayyWarehouseStore } from "../../../stores/SwayyWarehouseStore";
import SwayyOrderNotesPanel from "../../components/orders/SwayyOrderNotesPanel";
import SkuCopyRow from "../../../../../views/components/common/SkuCopyRow";
import SwayyCreateShipmentDialog from "../../components/shipments/SwayyCreateShipmentDialog";
import SwayyUpdateShipmentDialog from "../../components/shipments/SwayyUpdateShipmentDialog";
import SwayyShipmentInvoiceSection from "../../components/invoices/SwayyShipmentInvoiceSection";
import SwayyCreateCreditNoteDialog from "../../components/invoices/SwayyCreateCreditNoteDialog";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { userStore } from "../../../../../stores/sharedStores";
import { SwayyEmployeeRoles } from "../../../../../models/Employees/SwayyEmployeeRoles";

const INVOICE_POLL_MS = 45_000;

const cardSx = {
  p: { xs: 2.25, md: 2.5 },
  borderRadius: swayyRadii.lg,
  border: `1px solid ${swayyColors.border}`,
  bgcolor: swayyColors.white,
  boxShadow: "0 8px 28px rgba(0, 81, 84, 0.045)",
};

const thSx = {
  fontSize: "0.78rem",
  fontWeight: 700,
  color: swayyColors.textSecondary,
  letterSpacing: "0.04em",
  textTransform: "uppercase" as const,
  py: 1.4,
  px: 1.75,
  borderBottom: `1px solid ${swayyColors.border}`,
  backgroundColor: swayyColors.cloud,
  fontFamily: swayyFont,
  whiteSpace: "nowrap" as const,
};

const tdSx = {
  fontSize: "0.9rem",
  color: swayyColors.deepGreen,
  fontWeight: 400,
  py: 1.5,
  px: 1.75,
  borderBottom: `1px solid ${swayyColors.border}`,
  fontFamily: swayyFont,
};

const SectionTitle = ({
  children,
  compact,
}: {
  children: React.ReactNode;
  compact?: boolean;
}) => (
  <Box sx={{ mb: compact ? 1.5 : 2.25 }}>
    <CustomTypography
      variant="heading7"
      color={swayyColors.deepGreen}
      sx={{
        fontFamily: swayyFont,
        fontWeight: 600,
        fontSize: compact ? 12 : 13,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        lineHeight: 1.3,
      }}
    >
      {children}
    </CustomTypography>
    <Box
      sx={{
        mt: compact ? 0.9 : 1.1,
        height: "1px",
        width: "0%",
        bgcolor: "rgba(201, 228, 236, 0.95)",
        position: "relative",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: "-0.5px",
          width: compact ? 50 : 50,
          height: "2px",
          borderRadius: swayyRadii.full,
          bgcolor: swayyColors.pine,
        }}
      />
    </Box>
  </Box>
);

const StatusChip = ({
  label,
  tone = "pine",
}: {
  label: string;
  tone?: "pine" | "success" | "warning" | "danger" | "muted";
}) => {
  const styles: Record<string, { color: string; border: string; bg: string }> = {
    pine: {
      color: swayyColors.deepGreen,
      border: swayyColors.deepGreen,
      bg: swayyColors.white,
    },
    success: {
      color: swayyColors.success,
      border: swayyColors.success,
      bg: swayyColors.white,
    },
    warning: {
      color: swayyColors.warning,
      border: swayyColors.warning,
      bg: swayyColors.white,
    },
    danger: {
      color: swayyColors.danger,
      border: swayyColors.danger,
      bg: swayyColors.white,
    },
    muted: {
      color: swayyColors.textSecondary,
      border: swayyColors.border,
      bg: swayyColors.white,
    },
  };
  const toneStyle = styles[tone];

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1.1,
        py: 0.3,
        borderRadius: swayyRadii.sm,
        border: `1px solid ${toneStyle.border}`,
        bgcolor: toneStyle.bg,
        color: toneStyle.color,
        fontSize: 12,
        fontWeight: 600,
        fontFamily: swayyFont,
        lineHeight: 1.4,
        textTransform: "capitalize",
        whiteSpace: "nowrap",
      }}
    >
      {label.replace(/_/g, " ")}
    </Box>
  );
};

const AddressMetaRow = ({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) => {
  if (!value) return null;
  return (
    <Box sx={{ display: "flex", gap: 0.75, minWidth: 0 }}>
      <CustomTypography
        variant="body3"
        color={swayyColors.textSecondary}
        sx={{
          fontFamily: swayyFont,
          fontSize: 12,
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {label}:
      </CustomTypography>
      <CustomTypography
        variant="body2"
        color={swayyColors.deepGreen}
        sx={{
          fontFamily: swayyFont,
          fontSize: 13,
          fontWeight: 600,
          wordBreak: "break-word",
        }}
      >
        {value}
      </CustomTypography>
    </Box>
  );
};

const getAddressLabelChip = (address: SwayyOrderDetailAddress): string | null => {
  if (!address.label) return null;
  if (address.label.toLowerCase() === "other") {
    return address.label_other?.trim() || "Other";
  }
  return (
    address.label.charAt(0).toUpperCase() + address.label.slice(1).toLowerCase()
  );
};

const AddressBody = ({
  address,
}: {
  address?: SwayyOrderDetailAddress | null;
}) => {
  const hasAddress = Boolean(
    address &&
    (address.name ||
      address.line1 ||
      address.city ||
      address.pincode ||
      address.phone),
  );

  if (!hasAddress) {
    return (
      <Box
        sx={{
          height: "100%",
          minHeight: 120,
          p: 3,
          borderRadius: swayyRadii.md,
          bgcolor: swayyColors.pageBg,
          border: `1px dashed ${swayyColors.border}`,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <LocationOnOutlined
          sx={{
            fontSize: 32,
            color: swayyColors.textSecondary,
            mb: 1,
            opacity: 0.65,
          }}
        />
        <EmptyHint text="No address available." />
      </Box>
    );
  }

  const resolvedAddress = address as SwayyOrderDetailAddress;
  const phoneDisplay = [resolvedAddress.country_code, resolvedAddress.phone]
    .filter(Boolean)
    .join(" ");

  return (
    <Box sx={{ height: "100%" }}>
      <CustomTypography
        variant="body2"
        color={swayyColors.deepGreen}
        sx={{
          fontFamily: swayyFont,
          fontWeight: 700,
          fontSize: 15,
          lineHeight: 1.35,
          mb: 0.5,
        }}
      >
        {resolvedAddress.name || "—"}
      </CustomTypography>
      <CustomTypography
        variant="body2"
        color={swayyColors.textSecondary}
        sx={{
          fontFamily: swayyFont,
          lineHeight: 1.5,
          fontSize: 13,
          whiteSpace: "pre-line",
          wordBreak: "break-word",
        }}
      >
        {resolvedAddress.line1}
        {resolvedAddress.line2 ? `\n${resolvedAddress.line2}` : ""}
        {resolvedAddress.landmark ? `\n${resolvedAddress.landmark}` : ""}
      </CustomTypography>
      <Box
        sx={{
          mt: 1,
          display: "flex",
          flexDirection: "column",
          gap: 0.4,
        }}
      >
        <AddressMetaRow label="City" value={resolvedAddress.city} />
        <AddressMetaRow label="State" value={resolvedAddress.state} />
        <AddressMetaRow label="Pincode" value={resolvedAddress.pincode} />
        <AddressMetaRow label="Phone" value={phoneDisplay} />
        <AddressMetaRow label="Email" value={resolvedAddress.email} />
      </Box>
    </Box>
  );
};

const InfoRow = ({
  label,
  value,
  emphasize,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 2,
      py: 1.15,
      borderBottom: `1px solid ${swayyColors.border}`,
      "&:last-of-type": { borderBottom: "none" },
    }}
  >
    <CustomTypography
      variant="body2"
      color={swayyColors.textSecondary}
      sx={{ fontFamily: swayyFont, fontSize: 14, fontWeight: 500 }}
    >
      {label}
    </CustomTypography>
    <CustomTypography
      variant="body2"
      color={swayyColors.deepGreen}
      sx={{
        fontFamily: swayyFont,
        fontSize: 14,
        fontWeight: emphasize ? 700 : 600,
        textAlign: "right",
        wordBreak: "break-word",
      }}
    >
      {value || "—"}
    </CustomTypography>
  </Box>
);

const FactTile = ({
  label,
  value,
  fullWidth,
}: {
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: swayyRadii.md,
      bgcolor: swayyColors.white,
      border: `1px solid ${swayyColors.border}`,
      gridColumn: fullWidth ? "1 / -1" : undefined,
    }}
  >
    <CustomTypography
      variant="body2"
      color={swayyColors.textSecondary}
      sx={{
        fontFamily: swayyFont,
        fontSize: 13,
        fontWeight: 500,
        mb: 0.5,
      }}
    >
      {label}
    </CustomTypography>
    <Box
      sx={{
        fontFamily: swayyFont,
        fontSize: 15,
        fontWeight: 700,
        color: swayyColors.deepGreen,
        lineHeight: 1.35,
        wordBreak: "break-word",
      }}
    >
      {value}
    </Box>
  </Box>
);

const EmptyHint = ({ text }: { text: string }) => (
  <CustomTypography
    variant="body2"
    color={swayyColors.textSecondary}
    sx={{ fontFamily: swayyFont, fontSize: 14 }}
  >
    {text}
  </CustomTypography>
);

type ActivityItem = {
  id: string;
  at: string;
  title: string;
  detail?: string;
};

const SwayyOrderDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showError } = useNotification();
  const { swayyOrderDetailStore: store } = useStores();

  const handleLoadOrderDetail = async (orderId: string) => {
    try {
      await store.getOrderDetail(orderId);
    } catch {
      showError("Error", "Failed to load order details.");
    }
  };

  useEffect(() => {
    if (
      !userStore.hasSwayyPermission([
        SwayyEmployeeRoles.VIEW_ORDERS,
        SwayyEmployeeRoles.EDIT_ORDERS,
      ])
    ) {
      return;
    }

    if (!id) {
      showError("Error", "Order id is missing.");
      return;
    }
    void handleLoadOrderDetail(id);
    return () => {
      store.reset();
    };
  }, [id]);

  const hasPendingInvoices = store.invoices.some(
    (invoice) => String(invoice.status || "").toLowerCase() === "pending",
  );
  const hasPendingCreditNotes = Object.values(store.creditNotesByInvoice)
    .flat()
    .some((note) => String(note.status || "").toLowerCase() === "pending");
  const shouldPollDocuments = hasPendingInvoices || hasPendingCreditNotes;

  useEffect(() => {
    if (
      !id ||
      !shouldPollDocuments ||
      !userStore.hasSwayyPermission([
        SwayyEmployeeRoles.VIEW_ORDERS,
        SwayyEmployeeRoles.EDIT_ORDERS,
      ])
    ) {
      return;
    }

    const refreshDocuments = () => {
      void store.loadInvoices(id, { silent: true }).catch(() => {
        // Keep current list; next poll will retry.
      });
    };

    const intervalId = window.setInterval(refreshDocuments, INVOICE_POLL_MS);

    const handleFocus = () => refreshDocuments();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") refreshDocuments();
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [id, shouldPollDocuments, store]);

  useEffect(() => {
    if (
      !userStore.hasSwayyPermission([
        SwayyEmployeeRoles.VIEW_ORDERS,
        SwayyEmployeeRoles.EDIT_ORDERS,
      ])
    ) {
      return;
    }
    if (store.error) showError("Error", store.error);
  }, [store.error]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleViewImagesOfProduct = (
    productId: number,
    imageUrl?: string | null,
  ) => {
    if (!imageUrl) return;
    store.setSelectedProductImages(productId, [imageUrl]);
    store.managePopup("openImageGallery", "open");
  };

  const handleCloseImageGallery = useCallback(() => {
    store.managePopup("openImageGallery", "close");
  }, [store]);

  const handleOpenCreateShipment = async () => {
    try {
      await store.openCreateShipment();
    } catch {
      showError(
        "Error",
        store.shipmentError || "Failed to open create shipment.",
      );
    }
  };

  const handleOpenUpdateShipment = async (shipmentId: number) => {
    try {
      await store.openUpdateShipment(shipmentId);
    } catch {
      showError(
        "Error",
        store.shipmentError || "Failed to open shipment update.",
      );
    }
  };

  const handleOpenDeleteShipment = (shipmentId: number) => {
    store.openDeleteShipment(shipmentId);
  };

  const handleCreateShipment = async (payload: CreateSwayyShipmentPayload) => {
    try {
      await store.createShipment(payload);
    } catch {
      // Inline dialog error via shipmentError
    }
  };

  const handleUpdateShipment = async (payload: UpdateSwayyShipmentPayload) => {
    try {
      await store.updateShipment(payload);
    } catch {
      // Inline dialog error via shipmentError
    }
  };

  const handleDeleteShipment = async () => {
    try {
      await store.deleteShipment();
    } catch {
      // Inline error via shipmentError
    }
  };

  const handleDownloadDocument = (url: string) => {
    window.open(url, "_blank");
  };

  const handleRegenerateInvoice = async (
    documentId: number,
    againstInvoiceId?: number,
  ) => {
    try {
      await store.regenerateInvoice(documentId, { againstInvoiceId });
    } catch (error: any) {
      showError("Error", error?.message || "Failed to regenerate document.");
    }
  };

  const handleCreateCreditNote = async () => {
    try {
      await store.createCreditNote();
    } catch (error: any) {
      if (!store.creditNoteError) {
        showError("Error", error?.message || "Failed to create credit note.");
      }
    }
  };

  const handleLoadCreditNotes = async (invoiceId: number) => {
    try {
      await store.loadCreditNotes(invoiceId, { silent: true });
    } catch {
      // Section keeps empty/previous state; user can retry via regenerate/create.
    }
  };

  const handleCloseCreditNote = () => {
    store.managePopup("openCreditNote", "close");
  };

  const handleCloseCreateShipment = () => {
    store.managePopup("openCreateShipment", "close");
  };

  const handleCloseUpdateShipment = () => {
    store.managePopup("openUpdateShipment", "close");
  };

  const handleCloseDeleteShipment = () => {
    store.managePopup("openDeleteShipment", "close");
  };

  const galleryImages = useMemo(
    () =>
      store.selectedProductImages.map((img) => ({
        id: store.selectedProduct || 0,
        src: img,
      })),
    [store.selectedProduct, store.selectedProductImages],
  );

  const activityFeed = useMemo((): ActivityItem[] => {
    const order = store.order;
    if (!order) return [];
    const items: ActivityItem[] = [];

    order.timeline.forEach((event, index) => {
      items.push({
        id: `timeline-${index}-${event.created_at}`,
        at: event.created_at,
        title: getSwayyOrderStatusLabel(event.to_status),
        detail: event.from_status
          ? `${getSwayyOrderStatusLabel(event.from_status)} → ${getSwayyOrderStatusLabel(event.to_status)}${event.note ? ` · ${event.note}` : ""}`
          : event.note || undefined,
      });
    });

    if (order.confirmed_at) {
      items.push({
        id: "confirmed",
        at: order.confirmed_at,
        title: "Order confirmed",
      });
    }
    if (order.delivered_at) {
      items.push({
        id: "delivered",
        at: order.delivered_at,
        title: "Order delivered",
      });
    }
    if (order.cancelled_at) {
      items.push({
        id: "cancelled",
        at: order.cancelled_at,
        title: "Order cancelled",
      });
    }

    return items.sort(
      (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime(),
    );
  }, [store.order]);

  if (
    !userStore.hasSwayyPermission([
      SwayyEmployeeRoles.VIEW_ORDERS,
      SwayyEmployeeRoles.EDIT_ORDERS,
    ])
  ) {
    return <PermissionBlockLabel />;
  }

  if (store.loading) {
    return (
      <Box
        sx={{
          minHeight: "50vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: swayyColors.pageBg,
        }}
      >
        <CircularProgress sx={{ color: swayyColors.pine }} />
      </Box>
    );
  }

  if (!store.order) {
    return (
      <Box sx={{ p: { xs: 1.5, md: 2 }, bgcolor: swayyColors.pageBg }}>
        <Paper elevation={0} sx={{ ...cardSx, textAlign: "center", py: 6 }}>
          <AppButton
            label="Back to Orders"
            startIcon={<ArrowBack />}
            onClick={handleBack}
            customStyles={{
              borderRadius: swayyRadii.md,
              bgcolor: swayyColors.pine,
              color: swayyColors.white,
              mb: 2,
              "&:hover": { bgcolor: swayyColors.pineHover },
            }}
          />
          <CustomTypography
            variant="body2"
            color={swayyColors.textSecondary}
            sx={{ fontFamily: swayyFont }}
          >
            Order details could not be loaded.
          </CustomTypography>
        </Paper>
      </Box>
    );
  }

  const order = store.order;
  const primaryPayment = order.payments?.[0];
  const currency = order.currency || "INR";
  const shippingAddressLabel = order.shipping_address
    ? getAddressLabelChip(order.shipping_address)
    : null;
  const billingAddressLabel = order.billing_address
    ? getAddressLabelChip(order.billing_address)
    : null;
  const canCreateShipment =
    userStore.hasSwayyPermission(SwayyEmployeeRoles.EDIT_SHIPMENTS) &&
    SWAYY_SHIPPABLE_ORDER_STATUSES.has(order.status.toLowerCase()) &&
    order.items.some(
      (item) => (item.qty_shipped ?? 0) < item.qty,
    );

  const orderInvoices = store.invoices.filter(
    (invoice) => invoice.type !== "credit_note",
  );
  const creditNoteTargetInvoice = orderInvoices.find(
    (invoice) => Number(invoice.id) === Number(store.creditNoteInvoiceId),
  );

  const resolveShipmentInvoice = (
    shipment: (typeof order.shipments)[number],
  ) => {
    const byShipmentId = orderInvoices.find(
      (invoice) =>
        invoice.shipment_id != null &&
        Number(invoice.shipment_id) === Number(shipment.id),
    );
    if (byShipmentId) return byShipmentId;

    if (shipment.invoice?.id != null) {
      const byInvoiceId = orderInvoices.find(
        (invoice) => Number(invoice.id) === Number(shipment.invoice?.id),
      );
      if (byInvoiceId) return byInvoiceId;

      return {
        id: shipment.invoice.id,
        invoice_number:
          shipment.invoice.invoice_number || `#${shipment.invoice.id}`,
        type: "invoice" as const,
        status: shipment.invoice.status ?? undefined,
        issued_at: null,
        grand_total: 0,
        download_url: shipment.invoice.download_url ?? null,
        shipment_id: shipment.id,
      };
    }

    // When API omits shipment_id, attach the only invoice to the only shipment.
    if (
      order.shipments.length === 1 &&
      orderInvoices.length === 1
    ) {
      return orderInvoices[0];
    }

    return null;
  };

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2 },
        bgcolor: swayyColors.pageBg,
        minHeight: "100%",
      }}
    >
      {/* 1. Header — full width */}
      <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
            <IconButton
              onClick={handleBack}
              sx={{
                mt: 0.25,
                color: swayyColors.deepGreen,
                borderRadius: swayyRadii.sm,
                border: `1px solid ${swayyColors.border}`,
                width: 36,
                height: 36,
                "&:hover": { bgcolor: swayyColors.pineLight },
              }}
            >
              <ArrowBack fontSize="small" />
            </IconButton>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 1.25,
                }}
              >
                <CustomTypography
                  variant="heading5"
                  color={swayyColors.deepGreen}
                  sx={{ fontFamily: swayyFont, fontWeight: 700 }}
                >
                  {order.order_number}
                </CustomTypography>
                <StatusChip
                  label={order.status}
                  tone={getSwayyStatusTone(order.status)}
                />
                {order.payment_status ? (
                  <StatusChip
                    label={order.payment_status}
                    tone={getSwayyStatusTone(order.payment_status)}
                  />
                ) : null}
                {order.payment_method ? (
                  <StatusChip
                    label={order.payment_method}
                    tone={getSwayyStatusTone(order.payment_method)}
                  />
                ) : null}
                {primaryPayment?.gateway ? (
                  <StatusChip
                    label={primaryPayment.gateway}
                    tone="muted"
                  />
                ) : null}
              </Box>
              <CustomTypography
                variant="body2"
                color={swayyColors.textSecondary}
                sx={{ mt: 0.35, fontFamily: swayyFont }}
              >
                Placed {formatDate(order.placed_at)}
                {order.channel
                  ? ` · ${String(order.channel).toUpperCase()}`
                  : ""}
              </CustomTypography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Shipping / Customer / Billing — equal-height cards */}
      <Box
        sx={{
          mb: 2,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
          gap: 2,
          alignItems: "stretch",
        }}
      >


        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <SectionTitle compact>Customer</SectionTitle>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              p: 1.5,
              borderRadius: swayyRadii.md,
              bgcolor: swayyColors.pageBg,
              border: `1px solid ${swayyColors.border}`,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: 0.75,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                pb: 0.75,
                borderBottom: `1px solid ${swayyColors.border}`,
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: swayyRadii.md,
                  bgcolor: swayyColors.white,
                  border: `1px solid ${swayyColors.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: swayyColors.pine,
                  flexShrink: 0,
                }}
              >
                <PersonOutline sx={{ fontSize: 20 }} />
              </Box>
              <CustomTypography
                variant="body2"
                color={swayyColors.deepGreen}
                sx={{
                  fontFamily: swayyFont,
                  fontWeight: 700,
                  fontSize: 14,
                  lineHeight: 1.35,
                  wordBreak: "break-word",
                }}
              >
                {[order.customer?.first_name, order.customer?.last_name]
                  .filter(Boolean)
                  .join(" ") ||
                  order.shipping_address?.name ||
                  "—"}
              </CustomTypography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-evenly",
                flex: 1,
                minHeight: 0,
              }}
            >
              <InfoRow
                label="Phone"
                value={
                  order.customer?.phone ||
                  [
                    order.shipping_address?.country_code,
                    order.shipping_address?.phone,
                  ]
                    .filter(Boolean)
                    .join(" ") ||
                  "—"
                }
              />
              <InfoRow
                label="Email"
                value={
                  order.customer?.email ||
                  order.shipping_address?.email ||
                  "—"
                }
              />
            </Box>
          </Box>
        </Paper>
        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.25,
              mb: 1.5,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, "& > div": { mb: 0 } }}>
              <SectionTitle compact>Shipping address</SectionTitle>
            </Box>
            {shippingAddressLabel ? (
              <StatusChip label={shippingAddressLabel} tone="pine" />
            ) : null}
          </Box>
          <Box sx={{ flex: 1 }}>
            <AddressBody address={order.shipping_address} />
          </Box>
        </Paper>
        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.25,
              mb: 1.5,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, "& > div": { mb: 0 } }}>
              <SectionTitle compact>Billing address</SectionTitle>
            </Box>
            {billingAddressLabel ? (
              <StatusChip label={billingAddressLabel} tone="pine" />
            ) : null}
          </Box>
          <Box sx={{ flex: 1 }}>
            <AddressBody address={order.billing_address} />
          </Box>
        </Paper>
      </Box>

      {/* Products — full width */}
      <Paper elevation={0} sx={{ ...cardSx, p: 0, overflow: "hidden", mb: 2 }}>
        <Box sx={{ px: 2.25, pt: 2.25, pb: 0 }}>
          <SectionTitle>Products</SectionTitle>
        </Box>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...thSx, width: 48, textAlign: "center" }}>
                  #
                </TableCell>
                <TableCell sx={thSx}>Product</TableCell>
                <TableCell sx={{ ...thSx, textAlign: "center" }}>HSN</TableCell>
                <TableCell sx={{ ...thSx, textAlign: "center" }}>MRP</TableCell>
                <TableCell sx={{ ...thSx, textAlign: "center" }}>
                  Unit price
                </TableCell>
                <TableCell sx={{ ...thSx, textAlign: "center" }}>Qty</TableCell>
                <TableCell sx={{ ...thSx, textAlign: "center" }}>
                  Subtotal
                </TableCell>
                <TableCell sx={{ ...thSx, textAlign: "center" }}>Tax</TableCell>
                <TableCell sx={{ ...thSx, textAlign: "center" }}>Total</TableCell>
                <TableCell sx={{ ...thSx, textAlign: "center" }}>
                  Shipped
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {order.items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ ...tdSx, py: 4 }}>
                    <EmptyHint text="No items on this order." />
                  </TableCell>
                </TableRow>
              ) : (
                order.items.map((item, index) => (
                  <TableRow
                    key={item.id}
                    sx={{
                      "&:hover": { bgcolor: swayyColors.pineLight },
                      "&:last-child td": { borderBottom: 0 },
                    }}
                  >
                    <TableCell
                      sx={{
                        ...tdSx,
                        textAlign: "center",
                        fontWeight: 600,
                        color: swayyColors.textSecondary,
                      }}
                    >
                      {index + 1}
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                          minWidth: 220,
                        }}
                      >
                        <Box
                          sx={{
                            width: 52,
                            height: 52,
                            borderRadius: swayyRadii.md,
                            bgcolor: swayyColors.pageBg,
                            border: `1px solid ${swayyColors.border}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                            flexShrink: 0,
                            position: "relative",
                            cursor: item.image_url ? "pointer" : "default",
                            "&:hover .product-image-overlay": {
                              opacity: item.image_url ? 1 : 0,
                            },
                          }}
                          onClick={() =>
                            handleViewImagesOfProduct(
                              item.product_id,
                              item.image_url,
                            )
                          }
                        >
                          {item.image_url ? (
                            <Box
                              component="img"
                              src={item.image_url}
                              alt={item.name}
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <ImageOutlined
                              sx={{
                                fontSize: 22,
                                color: swayyColors.textSecondary,
                                opacity: 0.6,
                              }}
                            />
                          )}
                          {item.image_url ? (
                            <Box
                              className="product-image-overlay"
                              sx={{
                                position: "absolute",
                                inset: 0,
                                bgcolor: "rgba(0, 81, 84, 0.45)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                opacity: 0,
                                transition: "opacity 0.2s ease-in-out",
                              }}
                            >
                              <VisibilityOutlined
                                sx={{
                                  fontSize: 18,
                                  color: swayyColors.white,
                                }}
                              />
                            </Box>
                          ) : null}
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <CustomTypography
                            variant="body2"
                            color={swayyColors.deepGreen}
                            sx={{
                              fontFamily: swayyFont,
                              fontSize: 14,
                              fontWeight: 700,
                              lineHeight: 1.35,
                              wordBreak: "break-word",
                            }}
                          >
                            {item.name || "—"}
                          </CustomTypography>
                          <Box sx={{ mt: 0.35 }}>
                            <SkuCopyRow
                              sku={item.sku}
                              emptyFallback="—"
                              copyLabel="Copy SKU"
                              color={swayyColors.textSecondary}
                              variant="body3"
                              allowWrap
                              typographySx={{
                                fontFamily: swayyFont,
                                fontSize: 12,
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: "center" }}>
                      {item.hsn_code || "—"}
                    </TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: "center" }}>
                      {item.mrp != null
                        ? CurrencyHelper.formatCurrency(item.mrp, currency)
                        : "—"}
                    </TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: "center" }}>
                      {CurrencyHelper.formatCurrency(
                        item.unit_price,
                        currency,
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tdSx,
                        textAlign: "center",
                        fontWeight: 600,
                      }}
                    >
                      {item.qty}
                    </TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: "center" }}>
                      {CurrencyHelper.formatCurrency(
                        item.line_subtotal ?? 0,
                        currency,
                      )}
                    </TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: "center" }}>
                      <CustomTypography
                        variant="body2"
                        color={swayyColors.deepGreen}
                        sx={{
                          fontFamily: swayyFont,
                          fontSize: 14,
                          fontWeight: 600,
                        }}
                      >
                        {CurrencyHelper.formatCurrency(
                          item.line_tax ?? 0,
                          currency,
                        )}
                      </CustomTypography>
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tdSx,
                        textAlign: "center",
                        fontWeight: 700,
                      }}
                    >
                      {CurrencyHelper.formatCurrency(
                        item.line_total,
                        currency,
                      )}
                    </TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: "center" }}>
                      {item.qty_shipped ?? 0}/{item.qty}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Left stack (Shipment → Activity → Notes) | sticky Payment.
          Left stacks with normal gap; right holds in place while left scrolls (shared page scroll).
          Below 1024: Shipment → Payment → Activity → Notes */}
      <Box
        sx={{
          display: "grid",
          gap: 2,
          alignItems: "start",
          gridTemplateColumns: "1fr",
          gridTemplateAreas: `
            "shipment"
            "invoices"
            "activity"
            "notes"
          `,
          "@media (min-width: 1024px)": {
            gridTemplateColumns: "minmax(0, 1.6fr) minmax(0, 1fr)",
            gridTemplateAreas: `
              "left invoices"
            `,
          },
        }}
      >
        <Box
          sx={{
            display: "contents",
            "@media (min-width: 1024px)": {
              display: "flex",
              flexDirection: "column",
              gap: 2,
              gridArea: "left",
              minWidth: 0,
            },
          }}
        >
        {/* Shipment */}
        <Box
          sx={{
            gridArea: "shipment",
            minWidth: 0,
            "@media (min-width: 1024px)": { gridArea: "unset" },
          }}
        >
          <Paper elevation={0} sx={cardSx}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                mb: 2.25,
                flexWrap: "wrap",
              }}
            >
              <CustomTypography
                variant="heading7"
                color={swayyColors.deepGreen}
                sx={{
                  fontFamily: swayyFont,
                  fontWeight: 600,
                  fontSize: 13,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                }}
              >
                {order.shipments.length > 1
                  ? `Shipments (${order.shipments.length})`
                  : "Shipment"}
              </CustomTypography>
              {canCreateShipment && (
                <AppButton
                  label="Create shipment"
                  startIcon={<LocalShippingOutlined />}
                  onClick={() => {
                    void handleOpenCreateShipment();
                  }}
                  customStyles={{
                    borderRadius: swayyRadii.md,
                    minHeight: 40,
                    bgcolor: swayyColors.pine,
                    color: swayyColors.white,
                    "&:hover": { bgcolor: swayyColors.pineHover },
                  }}
                />
              )}
            </Box>
            {order.shipments.length === 0 ? (
              <Box
                sx={{
                  p: 3,
                  borderRadius: swayyRadii.md,
                  bgcolor: swayyColors.pageBg,
                  border: `1px dashed ${swayyColors.border}`,
                  textAlign: "center",
                }}
              >
                <LocalShippingOutlined
                  sx={{
                    fontSize: 32,
                    color: swayyColors.textSecondary,
                    mb: 1,
                    opacity: 0.65,
                  }}
                />
                <EmptyHint text="Shipment not created yet." />
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {order.shipments.map((shipment) => {
                  const shipmentInvoice = resolveShipmentInvoice(shipment);
                  const hasChipValue = (value?: string | null) => {
                    if (value == null) return false;
                    const trimmed = String(value).trim();
                    if (!trimmed) return false;
                    const lower = trimmed.toLowerCase();
                    return (
                      lower !== "null" &&
                      lower !== "undefined" &&
                      lower !== "—" &&
                      lower !== "-"
                    );
                  };
                  const headerChips = [
                    {
                      label: "S.ID",
                      value: getSwayyShipmentNumber(shipment),
                      copyable: true,
                    },
                    {
                      label: "Carrier",
                      value: shipment.carrier ?? null,
                    },
                    {
                      label: "Status",
                      value: shipment.status
                        ? getSwayyShipmentStatusLabel(String(shipment.status))
                        : null,
                      capitalize: true,
                    },
                    {
                      label: "AWB",
                      value: shipment.awb ?? null,
                    },
                  ].filter((chip) => hasChipValue(chip.value));

                  return (
                    <Box
                      key={shipment.id}
                      sx={{
                        p: 2,
                        borderRadius: swayyRadii.md,
                        bgcolor: swayyColors.pageBg,
                        border: `1px solid ${swayyColors.border}`,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 1.5,
                          mb: 1.5,
                          pb: 1.5,
                          borderBottom: `1px solid ${swayyColors.border}`,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.25,
                            flexWrap: "wrap",
                            minWidth: 0,
                            flex: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: swayyRadii.md,
                              bgcolor: swayyColors.white,
                              border: `1px solid ${swayyColors.border}`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: swayyColors.pine,
                              flexShrink: 0,
                            }}
                          >
                            <LocalShippingOutlined sx={{ fontSize: 20 }} />
                          </Box>

                          {headerChips.map((meta) => (
                            <Box
                              key={meta.label}
                              sx={{
                                display: "inline-flex",
                                alignItems: "stretch",
                                minWidth: 0,
                                border: `1px solid ${swayyColors.border}`,
                                borderRadius: `0 ${swayyRadii.full} ${swayyRadii.full} 0`,
                                overflow: "hidden",
                                bgcolor: swayyColors.white,
                                flexShrink: 0,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  px: 1.1,
                                  py: 0.55,
                                  bgcolor: swayyColors.pineLight,
                                  borderRight: `1px solid ${swayyColors.border}`,
                                  flexShrink: 0,
                                }}
                              >
                                <CustomTypography
                                  variant="body3"
                                  color={swayyColors.deepGreen}
                                  sx={{
                                    fontFamily: swayyFont,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.04em",
                                    lineHeight: 1,
                                  }}
                                >
                                  {meta.label}
                                </CustomTypography>
                              </Box>
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  px: meta.copyable ? 0.75 : 1.25,
                                  py: 0.55,
                                  pr: meta.copyable ? 0.5 : 1.25,
                                  minWidth: 0,
                                }}
                              >
                                {meta.copyable ? (
                                  <SkuCopyRow
                                    sku={String(meta.value || "")}
                                    emptyFallback="—"
                                    copyLabel="Copy shipment ID"
                                    color={swayyColors.deepGreen}
                                    fontWeight={700}
                                    typographySx={{
                                      fontFamily: swayyFont,
                                      fontSize: 13,
                                      lineHeight: 1.2,
                                    }}
                                  />
                                ) : (
                                  <CustomTypography
                                    variant="body2"
                                    color={swayyColors.deepGreen}
                                    sx={{
                                      fontFamily: swayyFont,
                                      fontSize: 13,
                                      fontWeight: 700,
                                      textTransform: meta.capitalize
                                        ? "capitalize"
                                        : "none",
                                      wordBreak: "break-word",
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {meta.value}
                                  </CustomTypography>
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Box>

                        {userStore.hasSwayyPermission(
                          SwayyEmployeeRoles.EDIT_SHIPMENTS,
                        ) ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                              flexShrink: 0,
                            }}
                          >
                            <Tooltip title="Update shipment">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  void handleOpenUpdateShipment(shipment.id);
                                }}
                                sx={{
                                  width: 34,
                                  height: 34,
                                  borderRadius: swayyRadii.sm,
                                  color: swayyColors.deepGreen,
                                  bgcolor: swayyColors.white,
                                  border: `1px solid ${swayyColors.border}`,
                                  "&:hover": {
                                    bgcolor: swayyColors.pineLight,
                                    borderColor: swayyColors.pine,
                                  },
                                }}
                              >
                                <EditOutlined sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            {canDeleteSwayyShipment(
                              String(shipment.status || ""),
                            ) && (
                              <Tooltip title="Delete shipment">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleOpenDeleteShipment(shipment.id)
                                  }
                                  sx={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: swayyRadii.sm,
                                    color: swayyColors.danger,
                                    bgcolor: "transparent",
                                    border: `1px solid ${swayyColors.danger}`,
                                    "&:hover": {
                                      bgcolor: "rgba(220, 38, 38, 0.06)",
                                    },
                                  }}
                                >
                                  <DeleteOutline sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        ) : null}
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.15,
                          mb: (shipment.items ?? []).length > 0 ? 1.5 : 0,
                        }}
                      >
                        <InfoRow
                          label="Shipped at"
                          value={
                            shipment.shipped_at
                              ? formatDate(shipment.shipped_at)
                              : "—"
                          }
                        />
                        <InfoRow
                          label="Expected delivery"
                          value={
                            shipment.expected_at
                              ? formatDate(shipment.expected_at)
                              : "—"
                          }
                        />
                        <InfoRow
                          label="Delivered at"
                          value={
                            shipment.delivered_at
                              ? formatDate(shipment.delivered_at)
                              : "—"
                          }
                        />
                        {shipment.tracking_url ? (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              gap: 2,
                              py: 1.15,
                            }}
                          >
                            <CustomTypography
                              variant="body2"
                              color={swayyColors.textSecondary}
                              sx={{
                                fontFamily: swayyFont,
                                fontSize: 14,
                                fontWeight: 500,
                              }}
                            >
                              Tracking
                            </CustomTypography>
                            <CustomTypography
                              variant="body2"
                              color={swayyColors.pine}
                              sx={{
                                fontFamily: swayyFont,
                                fontSize: 14,
                                fontWeight: 600,
                                textAlign: "right",
                                wordBreak: "break-all",
                                cursor: "pointer",
                                textDecoration: "underline",
                              }}
                              onClick={() =>
                                window.open(
                                  shipment.tracking_url || "",
                                  "_blank",
                                )
                              }
                            >
                              Track package
                            </CustomTypography>
                          </Box>
                        ) : null}
                      </Box>

                      <SwayyShipmentInvoiceSection
                        invoice={shipmentInvoice}
                        currency={currency}
                        creditNotes={
                          shipmentInvoice
                            ? store.creditNotesByInvoice[shipmentInvoice.id] ||
                            []
                            : []
                        }
                        creditNotesLoading={
                          shipmentInvoice != null &&
                          store.creditNotesLoadingId === shipmentInvoice.id
                        }
                        actionLoading={store.invoiceActionLoading}
                        onDownload={handleDownloadDocument}
                        onRegenerate={(documentId, againstInvoiceId) => {
                          void handleRegenerateInvoice(
                            documentId,
                            againstInvoiceId,
                          );
                        }}
                        onAddCreditNote={(invoiceId) => {
                          store.openCreditNote(invoiceId);
                        }}
                        onLoadCreditNotes={(invoiceId) => {
                          void handleLoadCreditNotes(invoiceId);
                        }}
                      />

                      {(shipment.items ?? []).length > 0 && (
                        <TableContainer
                          sx={{
                            mt: 0.5,
                            borderRadius: swayyRadii.md,
                            border: `1px solid ${swayyColors.border}`,
                            bgcolor: swayyColors.white,
                            overflowX: "auto",
                          }}
                        >
                          <Table
                            size="small"
                            sx={{ tableLayout: "fixed", width: "100%" }}
                          >
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  sx={{
                                    ...thSx,
                                    py: 1,
                                    px: 1.25,
                                    width: "40%",
                                    textAlign: "left",
                                  }}
                                >
                                  Product
                                </TableCell>
                                <TableCell
                                  sx={{
                                    ...thSx,
                                    py: 1,
                                    px: 1.25,
                                    width: "40%",
                                    textAlign: "left",
                                  }}
                                >
                                  SKU
                                </TableCell>
                                <TableCell
                                  sx={{
                                    ...thSx,
                                    py: 1,
                                    px: 1.25,
                                    width: "20%",
                                    textAlign: "center",
                                  }}
                                >
                                  Qty
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(shipment.items ?? []).map((item) => (
                                <TableRow
                                  key={`${shipment.id}-${item.order_item_id}-${item.sku}`}
                                  sx={{
                                    "&:last-child td": { borderBottom: 0 },
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      ...tdSx,
                                      py: 0.85,
                                      px: 1.25,
                                      fontWeight: 600,
                                      fontSize: 13,
                                      textAlign: "left",
                                    }}
                                  >
                                    {item.name || "—"}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      ...tdSx,
                                      py: 0.85,
                                      px: 1.25,
                                      textAlign: "left",
                                      fontSize: 12,
                                      color: swayyColors.textSecondary,

                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "inline-flex",
                                        justifyContent: "flex-start",
                                        alignItems: "center",
                                        width: "100%",
                                      }}
                                    >
                                      <SkuCopyRow
                                        sku={item.sku}
                                        emptyFallback="—"
                                        copyLabel="Copy SKU"
                                        color={swayyColors.textSecondary}
                                        variant="body3"
                                        allowWrap
                                        typographySx={{
                                          fontFamily: swayyFont,
                                          fontSize: 12,
                                        }}
                                      />
                                    </Box>
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      ...tdSx,
                                      py: 0.85,
                                      px: 1.25,
                                      textAlign: "center",
                                      fontWeight: 700,
                                      fontSize: 13,
                                    }}
                                  >
                                    {item.qty}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Box>

        {/* Activity — left column on desktop, after invoices when wrapped */}
        <Box
          sx={{
            gridArea: "activity",
            minWidth: 0,
            "@media (min-width: 1024px)": { gridArea: "unset" },
          }}
        >
          <Paper elevation={0} sx={cardSx}>
            <SectionTitle>Activity</SectionTitle>
            {activityFeed.length === 0 ? (
              <EmptyHint text="No activity yet." />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  width: "100%",
                  overflowX: "auto",
                  pt: 0.5,
                }}
              >
                {activityFeed.map((event, index) => {
                  const isLast = index === activityFeed.length - 1;
                  return (
                    <Box
                      key={event.id}
                      sx={{
                        flex: "0 0 auto",
                        width: 160,
                        display: "flex",
                        flexDirection: "column",
                        position: "relative",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 1,
                          position: "relative",
                        }}
                      >
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor:
                              index === 0
                                ? swayyColors.pine
                                : swayyColors.deepGreenLight,
                            flexShrink: 0,
                            zIndex: 1,
                          }}
                        />
                        {!isLast && (
                          <Box
                            sx={{
                              position: "absolute",
                              left: 10,
                              width: "calc(100% + 16px)",
                              height: 2,
                              bgcolor: swayyColors.border,
                            }}
                          />
                        )}
                      </Box>
                      <CustomTypography
                        variant="body2"
                        color={swayyColors.deepGreen}
                        sx={{
                          fontFamily: swayyFont,
                          fontWeight: 600,
                          fontSize: 14,
                          lineHeight: 1.35,
                          mb: 0.25,
                        }}
                      >
                        {event.title}
                      </CustomTypography>
                      {event.detail && (
                        <CustomTypography
                          variant="body2"
                          color={swayyColors.textSecondary}
                          sx={{ fontFamily: swayyFont, fontSize: 13 }}
                        >
                          {event.detail}
                        </CustomTypography>
                      )}
                      <CustomTypography
                        variant="body3"
                        color={swayyColors.textSecondary}
                        sx={{
                          mt: 0.35,
                          fontFamily: swayyFont,
                          fontSize: 12,
                        }}
                      >
                        {formatDate(event.at)}
                      </CustomTypography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Box>

        {/* Notes — left column on desktop, last when wrapped */}
        <Box
          sx={{
            gridArea: "notes",
            minWidth: 0,
            "@media (min-width: 1024px)": { gridArea: "unset" },
          }}
        >
          <Paper elevation={0} sx={cardSx}>
            <SwayyOrderNotesPanel customerNote={order.customer_note} />
          </Paper>
        </Box>
        </Box>

        {/* Payment / Refunds — sticks while taller left column scrolls (same page scroll) */}
        <Box
          sx={{
            gridArea: "invoices",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minWidth: 0,
            alignSelf: "start",
            "@media (min-width: 1024px)": {
              position: "sticky",
              // Fixed AppBar toolbar is 56px
              top: 72,
            },
          }}
        >
          <Paper elevation={0} sx={cardSx}>
            <SectionTitle>Payment summary</SectionTitle>
            <Box
              sx={{
                p: 2,
                borderRadius: swayyRadii.md,
                bgcolor: swayyColors.pageBg,
                border: `1px solid ${swayyColors.border}`,
              }}
            >
              <InfoRow
                label="Subtotal"
                value={CurrencyHelper.formatCurrency(
                  order.totals.item_subtotal,
                  currency,
                )}
              />
              {order.totals.discount_total > 0 && (
                <InfoRow
                  label="Discount"
                  value={`- ${CurrencyHelper.formatCurrency(order.totals.discount_total, currency)}`}
                />
              )}
              <InfoRow
                label="Shipping"
                value={CurrencyHelper.formatCurrency(
                  order.totals.shipping_total,
                  currency,
                )}
              />
              {order.totals.cod_surcharge > 0 && (
                <InfoRow
                  label="COD surcharge"
                  value={CurrencyHelper.formatCurrency(
                    order.totals.cod_surcharge,
                    currency,
                  )}
                />
              )}
              <InfoRow
                label="Tax"
                value={CurrencyHelper.formatCurrency(
                  order.totals.tax_total,
                  currency,
                )}
              />
              {order.coupon_code && (
                <InfoRow label="Coupon" value={order.coupon_code} />
              )}
            </Box>
            <Box
              sx={{
                mt: 1.5,
                p: 2,
                borderRadius: swayyRadii.md,
                bgcolor: swayyColors.pineLight,
                border: `1px solid ${swayyColors.border}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CustomTypography
                variant="body2"
                color={swayyColors.deepGreen}
                sx={{ fontFamily: swayyFont, fontWeight: 700, fontSize: 15 }}
              >
                Grand total
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={swayyColors.pine}
                sx={{ fontFamily: swayyFont, fontWeight: 700, fontSize: 20 }}
              >
                {CurrencyHelper.formatCurrency(
                  order.totals.grand_total,
                  currency,
                )}
              </CustomTypography>
            </Box>
          </Paper>

          <Paper elevation={0} sx={cardSx}>
            <SectionTitle>Payment details</SectionTitle>
            <Box
              sx={{
                p: 2,
                borderRadius: swayyRadii.md,
                bgcolor: swayyColors.pageBg,
                border: `1px solid ${swayyColors.border}`,
              }}
            >
              <InfoRow
                label="Payment status"
                value={(order.payment_status || "—").replace(/_/g, " ")}
              />
              <InfoRow
                label="Payment method"
                value={(order.payment_method || "—").replace(/_/g, " ")}
              />
              <InfoRow
                label="Gateway"
                value={primaryPayment?.gateway || "—"}
              />
              <InfoRow
                label="Payment mode"
                value={
                  (primaryPayment?.method || "—").replace(/_/g, " ")
                }
              />
              <InfoRow
                label="Paid amount"
                value={
                  primaryPayment?.amount != null
                    ? CurrencyHelper.formatCurrency(
                      Number(primaryPayment.amount),
                      primaryPayment.currency || currency,
                    )
                    : "—"
                }
              />
              <InfoRow
                label="Amount refunded"
                value={
                  primaryPayment?.amount_refunded != null
                    ? CurrencyHelper.formatCurrency(
                      Number(primaryPayment.amount_refunded),
                      primaryPayment.currency || currency,
                    )
                    : "—"
                }
              />
              <InfoRow
                label="Gateway status"
                value={(primaryPayment?.status || "—").replace(/_/g, " ")}
              />
              <InfoRow
                label="Paid on"
                value={
                  primaryPayment?.created_at
                    ? formatDate(primaryPayment.created_at)
                    : "—"
                }
              />
            </Box>

            {(order.payments ?? []).length > 1 && (
              <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                <CustomTypography
                  variant="body3"
                  color={swayyColors.textSecondary}
                  sx={{
                    fontFamily: swayyFont,
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  Other payments
                </CustomTypography>
                {order.payments.slice(1).map((payment) => (
                  <Box
                    key={payment.id}
                    sx={{
                      p: 1.5,
                      borderRadius: swayyRadii.md,
                      bgcolor: swayyColors.pageBg,
                      border: `1px solid ${swayyColors.border}`,
                    }}
                  >
                    <InfoRow
                      label="Gateway"
                      value={payment.gateway || "—"}
                    />
                    <InfoRow
                      label="Mode"
                      value={(payment.method || "—").replace(/_/g, " ")}
                    />
                    <InfoRow
                      label="Amount"
                      value={CurrencyHelper.formatCurrency(
                        Number(payment.amount ?? 0),
                        payment.currency || currency,
                      )}
                    />
                    <InfoRow
                      label="Status"
                      value={(payment.status || "—").replace(/_/g, " ")}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          {order.refunds.length > 0 && (
            <Paper elevation={0} sx={cardSx}>
              <SectionTitle>Refunds</SectionTitle>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {order.refunds.map((refund, index) => (
                  <Box
                    key={refund.id ?? index}
                    sx={{
                      p: 2,
                      borderRadius: swayyRadii.md,
                      bgcolor: swayyColors.pageBg,
                      border: `1px solid ${swayyColors.border}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1.5,
                        mb: 1.75,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                        }}
                      >
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: swayyRadii.md,
                            bgcolor: "rgba(217, 119, 6, 0.12)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: swayyColors.warning,
                            flexShrink: 0,
                          }}
                        >
                          <ReplayOutlined sx={{ fontSize: 22 }} />
                        </Box>
                        <Box>
                          <CustomTypography
                            variant="body2"
                            color={swayyColors.textSecondary}
                            sx={{ fontFamily: swayyFont, fontSize: 13 }}
                          >
                            Refund amount
                          </CustomTypography>
                          <CustomTypography
                            variant="body2"
                            color={swayyColors.deepGreen}
                            sx={{
                              fontFamily: swayyFont,
                              fontWeight: 700,
                              fontSize: 18,
                            }}
                          >
                            {refund.amount != null
                              ? CurrencyHelper.formatCurrency(
                                Number(refund.amount),
                                currency,
                              )
                              : "—"}
                          </CustomTypography>
                        </Box>
                      </Box>
                      <StatusChip
                        label={String(refund.status || "—")}
                        tone="warning"
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr",
                        gap: 1.25,
                      }}
                    >
                      <FactTile
                        label="Reason"
                        value={String(refund.reason || "—").replace(/_/g, " ")}
                      />
                      <FactTile
                        label="Processed on"
                        value={formatDate(
                          refund.processed_at || refund.created_at,
                        )}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
        </Box>

      </Box>

      <SwayyCreateShipmentDialog
        open={store.popupState.openCreateShipment}
        items={order.items}
        warehouses={swayyWarehouseStore.warehouses}
        carriers={swayyCarrierStore.carriers}
        loading={store.shipmentSaving}
        lookupsLoading={store.lookupsLoading}
        error={store.shipmentError}
        onClose={handleCloseCreateShipment}
        onSubmit={(payload) => {
          void handleCreateShipment(payload);
        }}
      />

      <SwayyUpdateShipmentDialog
        open={store.popupState.openUpdateShipment}
        shipment={store.selectedShipment}
        carriers={swayyCarrierStore.carriers}
        loading={store.shipmentSaving}
        error={store.shipmentError}
        onClose={handleCloseUpdateShipment}
        onSubmit={(payload) => {
          void handleUpdateShipment(payload);
        }}
      />

      <ConfirmPopup
        mode="swayy"
        open={store.popupState.openDeleteShipment}
        title="Delete shipment?"
        message="Only shipments in Created status can be deleted. Inventory and shipped qty will be restored, and the pending invoice will be cancelled."
        cancelText="Keep shipment"
        confirmText={store.shipmentSaving ? "Deleting..." : "Delete shipment"}
        loading={store.shipmentSaving}
        error={store.shipmentError}
        onCancel={handleCloseDeleteShipment}
        onConfirm={() => {
          void handleDeleteShipment();
        }}
      />

      <SwayyCreateCreditNoteDialog
        open={store.popupState.openCreditNote}
        invoiceNumber={creditNoteTargetInvoice?.invoice_number}
        reason={store.creditNoteReason}
        remarks={store.creditNoteRemarks}
        amount={store.creditNoteAmount}
        loading={store.creditNoteSaving}
        error={store.creditNoteError}
        onClose={handleCloseCreditNote}
        onReasonChange={store.setCreditNoteReason}
        onRemarksChange={store.setCreditNoteRemarks}
        onAmountChange={store.setCreditNoteAmount}
        onConfirm={() => {
          void handleCreateCreditNote();
        }}
      />

      {store.popupState.openImageGallery ? (
        <ImageGalleryPopup
          key="productImages"
          open={store.popupState.openImageGallery}
          onClose={handleCloseImageGallery}
          images={galleryImages}
          title="Product Images"
        />
      ) : null}
    </Box>
  );
};

export default observer(SwayyOrderDetail);
